import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import ConnString from './ConnString.js';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material';

const Connstring2 = ConnString.connstring;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('info');
  const [message, setMessage] = useState('Enter your email address to receive a password reset link.');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!email) {
      setStatus('error');
      setMessage('Please enter your email address.');
      return;
    }

    setLoading(true);
    setStatus('info');
    setMessage('Sending reset link...');

    try {
      const response = await fetch(`${Connstring2}/api/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email }),
      });
      const data = await response.json();

      if (response.ok && data.success) {
        setStatus('success');
        setMessage(data.message || 'A password reset email has been sent if that address exists.');
      } else {
        setStatus('error');
        setMessage(data?.message || 'Unable to send reset email. Please try again.');
      }
    } catch (err) {
      console.error('Forgot password request failed:', err);
      setStatus('error');
      setMessage('An error occurred while sending the reset link. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={styles.pageWrapper}>
      <Paper elevation={16} sx={styles.formPanel}>
        <Box sx={styles.formHeader}>
          <Typography variant="h5" fontWeight={700}>
            Reset your password
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Enter the email address associated with your account and we'll send a link to reset your password.
          </Typography>
        </Box>

        {message && (
          <Alert severity={status === 'error' ? 'error' : status} sx={{ mb: 3 }}>
            {message}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={styles.form} noValidate>
          <TextField
            label="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            required
            fullWidth
            autoComplete="username"
            autoFocus
            sx={{ backgroundColor: '#f9fbff' }}
          />
          <Button type="submit" variant="contained" size="large" fullWidth disabled={loading}>
            {loading ? 'Sending...' : 'Send reset link'}
          </Button>
        </Box>

        <Box sx={styles.footerRow}>
          <Link component={RouterLink} to="/" underline="hover">
            Back to sign in
          </Link>
          <Button variant="text" onClick={() => navigate('/')}>
            Return to login
          </Button>
        </Box>
      </Paper>

      <Backdrop sx={styles.backdrop} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

const styles = {
  pageWrapper: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    px: 3,
    py: 6,
    background: 'linear-gradient(135deg, #1f3d7a 0%, #0b2049 100%)',
  },
  formPanel: {
    width: '100%',
    maxWidth: 480,
    borderRadius: 4,
    p: 4,
    backgroundColor: '#f8fbff',
    boxShadow: '0 30px 60px rgba(15,23,42,0.16)',
  },
  formHeader: {
    mb: 3,
  },
  form: {
    display: 'grid',
    gap: 20,
  },
  footerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mt: 3,
    flexWrap: 'wrap',
    gap: 2,
  },
  backdrop: {
    color: '#fff',
    zIndex: (theme) => theme.zIndex.drawer + 1,
  },
};

export default ForgotPassword;
